import { useState, useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { UserProfile } from '@/lib/types';
import { userProfileService } from '@/lib/userProfileService';

export function useUserProfile() {
  const { user } = useUser();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshProfile = async () => {
    if (!user?.id) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedProfile = await userProfileService.getUserProfile(user.id);
      setProfile(fetchedProfile);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshProfile();
  }, [user?.id]);

  const createProfile = async (profileData: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.id) throw new Error('User not authenticated');

    try {
      setError(null);
      const newProfile = await userProfileService.createUserProfile(profileData);
      setProfile(newProfile);
      return newProfile;
    } catch (err) {
      console.error('Error creating user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to create profile');
      throw err;
    }
  };

  const updateProfile = async (updates: Partial<Omit<UserProfile, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>) => {
    if (!user?.id) throw new Error('User not authenticated');

    try {
      setError(null);
      const updatedProfile = await userProfileService.updateUserProfile(user.id, updates);
      setProfile(updatedProfile);
      return updatedProfile;
    } catch (err) {
      console.error('Error updating user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      throw err;
    }
  };

  const upsertProfile = async (profileData: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.id) throw new Error('User not authenticated');

    try {
      setError(null);
      const upsertedProfile = await userProfileService.upsertUserProfile(profileData);
      setProfile(upsertedProfile);
      return upsertedProfile;
    } catch (err) {
      console.error('Error upserting user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to save profile');
      throw err;
    }
  };

  const deleteProfile = async () => {
    if (!user?.id) throw new Error('User not authenticated');

    try {
      setError(null);
      const success = await userProfileService.deleteUserProfile(user.id);
      if (success) {
        setProfile(null);
      }
      return success;
    } catch (err) {
      console.error('Error deleting user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete profile');
      throw err;
    }
  };

  return {
    profile,
    loading,
    error,
    refreshProfile,
    createProfile,
    updateProfile,
    upsertProfile,
    deleteProfile,
  };
} 